import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Copy, Share2 } from "lucide-react";
import {
  americanError,
  americanToDecimal,
  decimalError,
  decimalToAmerican,
  decimalToFractional,
  decimalToImpliedProbability,
  formatAmerican,
  formatDecimal,
  formatPercent,
  formatUSD,
  fractionalError,
  fractionalToDecimal,
  parseFractional,
} from "@/lib/oddsMath";
import { CONVERTER } from "@/constants/testIds";
import { EVENTS, track } from "@/lib/analytics";

const TABS = [
  { key: "american", label: "American", testId: CONVERTER.tabAmerican },
  { key: "decimal", label: "Decimal", testId: CONVERTER.tabDecimal },
  { key: "fractional", label: "Fractional", testId: CONVERTER.tabFractional },
];

const EXAMPLES = {
  american: [
    { label: "-110", value: "-110" },
    { label: "+150", value: "+150" },
    { label: "+200", value: "+200" },
    { label: "-250", value: "-250" },
  ],
  decimal: [
    { label: "1.91", value: "1.91" },
    { label: "2.50", value: "2.50" },
    { label: "3.00", value: "3.00" },
  ],
  fractional: [
    { label: "10/11", value: "10/11" },
    { label: "3/2", value: "3/2" },
    { label: "5/1", value: "5/1" },
  ],
};

export const OddsConverter = () => {
  const [tab, setTab] = useState("american");
  const [americanVal, setAmericanVal] = useState("");
  const [decimalVal, setDecimalVal] = useState("");
  const [fractionalVal, setFractionalVal] = useState("");

  const getInputValue = () => {
    if (tab === "american") return americanVal;
    if (tab === "decimal") return decimalVal;
    return fractionalVal;
  };

  const validationError = useMemo(() => {
    const raw = getInputValue();
    if (raw === "") return "";
    if (tab === "american") return americanError(raw);
    if (tab === "decimal") return decimalError(raw);
    return fractionalError(raw);
  }, [tab, americanVal, decimalVal, fractionalVal]);

  const result = useMemo(() => {
    const raw = getInputValue();
    if (raw === "" || validationError) return null;
    let decimal;
    try {
      if (tab === "american") {
        decimal = americanToDecimal(Number(String(raw).trim()));
      } else if (tab === "decimal") {
        decimal = Number(String(raw).trim());
      } else {
        const parsed = parseFractional(raw);
        if (!parsed) return null;
        decimal = fractionalToDecimal(parsed.num, parsed.den);
      }
      if (!Number.isFinite(decimal) || decimal <= 1) return null;
      const american = decimalToAmerican(decimal);
      const fractional = decimalToFractional(decimal);
      const implied = decimalToImpliedProbability(decimal);
      const payout = 100 * decimal;
      const profit = payout - 100;
      return { american, decimal, fractional, implied, payout, profit };
    } catch {
      return null;
    }
  }, [tab, americanVal, decimalVal, fractionalVal, validationError]);

  // Debounced odds_converted event — non-PII (only input format).
  const convertFireTimer = useRef(null);
  useEffect(() => {
    if (convertFireTimer.current) clearTimeout(convertFireTimer.current);
    if (!result) return undefined;
    convertFireTimer.current = setTimeout(() => {
      track(EVENTS.ODDS_CONVERTED, { input_format: tab });
    }, 600);
    return () => {
      if (convertFireTimer.current) clearTimeout(convertFireTimer.current);
    };
  }, [result, tab]);

  const setInputValue = (value) => {
    if (tab === "american") setAmericanVal(value);
    else if (tab === "decimal") setDecimalVal(value);
    else setFractionalVal(value);
  };

  const currentTestId =
    tab === "american"
      ? CONVERTER.inputAmerican
      : tab === "decimal"
        ? CONVERTER.inputDecimal
        : CONVERTER.inputFractional;

  const inputPlaceholder =
    tab === "american" ? "-110 or +150" : tab === "decimal" ? "1.91" : "3/2";

  const resultsText = () => {
    if (!result) return "";
    return [
      `Parlay Assistant — Odds Converter`,
      `American: ${formatAmerican(result.american)}`,
      `Decimal: ${formatDecimal(result.decimal)}`,
      `Fractional: ${result.fractional.text}`,
      `Implied Probability: ${formatPercent(result.implied)}`,
      `Profit on $100: ${formatUSD(result.profit)}`,
      `Total Payout on $100: ${formatUSD(result.payout)}`,
    ].join("\n");
  };

  const copyResults = async () => {
    if (!result) {
      toast.error("Enter valid odds first.");
      return;
    }
    try {
      await navigator.clipboard.writeText(resultsText());
      track(EVENTS.RESULTS_COPIED, { tool: "converter", input_format: tab });
      toast.success("Results copied to clipboard.");
    } catch {
      toast.error("Copy failed. Please try again.");
    }
  };

  const shareResults = async () => {
    if (!result) {
      toast.error("Enter valid odds first.");
      return;
    }
    const text = resultsText();
    track(EVENTS.RESULTS_SHARED, {
      tool: "converter",
      input_format: tab,
      channel: navigator.share ? "web-share" : "clipboard",
    });
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Parlay Assistant — Odds",
          text,
          url: typeof window !== "undefined" ? window.location.href : undefined,
        });
      } catch {
        /* canceled */
      }
      return;
    }
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Sharing not supported here. Results copied instead.");
    } catch {
      toast.error("Sharing not available in this browser.");
    }
  };

  return (
    <section
      id="odds-converter"
      data-testid={CONVERTER.card}
      className="pa-card p-6 md:p-8"
    >
      <div>
        <p className="font-display text-emerald-700 text-xs tracking-[0.2em] uppercase">
          Tool 02
        </p>
        <h2 className="font-display text-3xl md:text-4xl uppercase tracking-tight text-slate-900 mt-1">
          Betting Odds Converter
        </h2>
        <p className="text-slate-600 mt-2 text-sm md:text-base max-w-xl">
          Enter American, decimal, or fractional odds and instantly see the equivalent formats.
        </p>
      </div>

      <div className="mt-6">
        <div
          role="tablist"
          aria-label="Input format"
          className="pa-tabs"
        >
          {TABS.map((t) => (
            <button
              key={t.key}
              role="tab"
              aria-selected={tab === t.key}
              data-active={tab === t.key ? "true" : "false"}
              data-testid={t.testId}
              className="pa-tab"
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="mt-4">
          <label
            htmlFor="odds-input"
            className="block text-xs font-semibold uppercase tracking-wider text-slate-600"
          >
            {tab === "american"
              ? "American Odds"
              : tab === "decimal"
                ? "Decimal Odds"
                : "Fractional Odds"}
          </label>
          <input
            id="odds-input"
            data-testid={currentTestId}
            value={getInputValue()}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder={inputPlaceholder}
            type="text"
            inputMode={tab === "decimal" ? "decimal" : "text"}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            data-invalid={validationError ? "true" : "false"}
            aria-invalid={!!validationError}
            className="pa-input mt-2 font-mono-num text-lg"
          />
          {validationError && (
            <p
              data-testid={CONVERTER.inputError}
              className="pa-inline-error"
            >
              {validationError}
            </p>
          )}

          <div className="mt-4">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-600">
              Examples
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              {EXAMPLES[tab].map((ex, i) => (
                <button
                  key={ex.label}
                  type="button"
                  data-testid={`${CONVERTER.exampleBtn}-${tab}-${i}`}
                  className="pa-chip"
                  onClick={() => setInputValue(ex.value)}
                >
                  {ex.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="mt-8">
        <div className="flex items-center justify-between">
          <h3 className="font-display text-xl uppercase tracking-wide text-slate-900">
            Results
          </h3>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={copyResults}
              data-testid={CONVERTER.copyResults}
              className="pa-btn pa-btn-ghost !py-2 !text-xs"
            >
              <Copy size={14} /> Copy
            </button>
            <button
              type="button"
              onClick={shareResults}
              data-testid={CONVERTER.shareResults}
              className="pa-btn pa-btn-ghost !py-2 !text-xs"
            >
              <Share2 size={14} /> Share
            </button>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3">
          <div className="pa-result">
            <span className="pa-result-label">American</span>
            <span
              data-testid={CONVERTER.outAmerican}
              className="pa-result-value font-mono-num"
            >
              {result ? formatAmerican(result.american) : "—"}
            </span>
          </div>
          <div className="pa-result">
            <span className="pa-result-label">Decimal</span>
            <span
              data-testid={CONVERTER.outDecimal}
              className="pa-result-value font-mono-num"
            >
              {result ? formatDecimal(result.decimal) : "—"}
            </span>
          </div>
          <div className="pa-result">
            <span className="pa-result-label">Fractional</span>
            <span
              data-testid={CONVERTER.outFractional}
              className="pa-result-value font-mono-num"
            >
              {result ? result.fractional.text : "—"}
            </span>
          </div>
          <div className="pa-result">
            <span className="pa-result-label">Implied Probability</span>
            <span
              data-testid={CONVERTER.outImplied}
              className="pa-result-value font-mono-num"
            >
              {result ? formatPercent(result.implied) : "—"}
            </span>
          </div>
          <div className="pa-result">
            <span className="pa-result-label">Profit on $100</span>
            <span
              data-testid={CONVERTER.outProfit}
              className="pa-result-value font-mono-num"
              style={{ color: result ? "#047857" : undefined }}
            >
              {result ? formatUSD(result.profit) : "—"}
            </span>
          </div>
          <div className="pa-result pa-result-emerald">
            <span className="pa-result-label">Total Payout on $100</span>
            <span
              data-testid={CONVERTER.outPayout}
              className="pa-result-value font-mono-num"
            >
              {result ? formatUSD(result.payout) : "—"}
            </span>
          </div>
        </div>

        {!result && (
          <p className="mt-4 text-sm text-slate-500">
            Enter valid odds in the selected format to view the conversions.
          </p>
        )}
      </div>
    </section>
  );
};

export default OddsConverter;
